import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import { Seo } from '@/components/Seo';
import { supabase } from '@/lib/supabaseClient';
import { useSiteDesign } from '@/hooks/useSiteDesign';
import ShippingV2 from '@/pages/info/v2/ShippingV2';

export default function ShippingReturnsPage() {
  const { isNew } = useSiteDesign();
  return isNew ? <ShippingV2 /> : <ClassicShippingReturnsPage />;
}

function ClassicShippingReturnsPage() {
  const { t } = useTranslation();

  // Real delivery charges — single source of truth is core_shipping_rates,
  // the same table ERP's Delivery Merchants settings writes to (used
  // everywhere: public checkout, admin New Order, order-placement
  // validation). Editing the price here used to be a dead end: a
  // site-content text field with no connection to what checkout actually
  // charges, so the two could silently disagree. Defaults match what's
  // in the DB right now, shown while the real values load.
  const [rates, setRates] = useState({ dhaka: 60, outside: 120, freeThreshold: 3000 });

  useEffect(() => {
    let mounted = true;
    supabase
      .from('core_shipping_rates')
      .select('dhaka_charge_bdt, outside_dhaka_charge_bdt, free_shipping_threshold_bdt')
      .eq('id', 1)
      .maybeSingle()
      .then(({ data }) => {
        if (mounted && data) {
          setRates({
            dhaka: data.dhaka_charge_bdt,
            outside: data.outside_dhaka_charge_bdt,
            freeThreshold: data.free_shipping_threshold_bdt,
          });
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const dhakaPrice = `৳${rates.dhaka}`;
  const outsidePrice = `৳${rates.outside}`;
  const freeThreshold = `৳${rates.freeThreshold.toLocaleString()}`;

  const shippingOptions = [
    {
      icon: 'ri-truck-line',
      title: t('shipping.dhaka.title'),
      price: dhakaPrice,
      time: t('shipping.dhaka.time'),
      desc: t('shipping.dhaka.desc', { price: dhakaPrice }),
    },
    {
      icon: 'ri-truck-line',
      title: t('shipping.outside.title'),
      price: outsidePrice,
      time: t('shipping.outside.time'),
      desc: t('shipping.outside.desc', { price: outsidePrice }),
    },
    {
      icon: 'ri-gift-line',
      title: t('shipping.free.title'),
      price: '৳0',
      time: t('shipping.track.title'),
      desc: t('shipping.free.desc', { threshold: freeThreshold }),
    },
  ];

  const returnSteps = [
    { num: '01', title: t('shipping.step1.title'), desc: t('shipping.step1.desc') },
    { num: '02', title: t('shipping.step2.title'), desc: t('shipping.step2.desc') },
    { num: '03', title: t('shipping.step3.title'), desc: t('shipping.step3.desc') },
  ];

  const conditionsOk = [
    t('shipping.condition1'),
    t('shipping.condition2'),
    t('shipping.condition3'),
  ];
  const conditionsNot = [
    t('shipping.condition4'),
    t('shipping.condition5'),
  ];

  return (
    <main className="w-full min-h-screen bg-white font-sans">
      <Seo pageKey="shipping" />
      <SharedNavbar />

      {/* ===== HERO ===== */}
      <section className="w-full bg-[#1A1A1A] pt-28 pb-16 md:pt-32 md:pb-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-6 h-px bg-[#7B1113]" />
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#7B1113] font-medium">
              {t('shipping.hero.label')}
            </span>
            <div className="w-6 h-px bg-[#7B1113]" />
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.15] mb-4">
            {t('shipping.hero.heading')}
          </h1>
          <p className="text-sm text-white/40 font-light max-w-lg mx-auto">
            {t('shipping.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* ===== SHIPPING OPTIONS ===== */}
      <section className="w-full bg-white py-16 md:py-24 px-6 md:px-12 lg:px-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2A2A2A] mb-10 text-center">
            {t('shipping.options.heading')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {shippingOptions.map((opt) => (
              <div
                key={opt.title}
                className="bg-[#F0EFED] rounded-2xl p-6 flex flex-col items-center text-center gap-3"
              >
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center">
                  <i className={`${opt.icon} text-[#7B1113] text-xl`} />
                </div>
                <h3 className="font-serif text-lg font-bold text-[#2A2A2A]">
                  {opt.title}
                </h3>
                <span className="font-serif text-2xl font-bold text-[#7B1113]">
                  {opt.price}
                </span>
                <span className="text-[11px] text-[#8A8A8A] uppercase tracking-wide">
                  {opt.time}
                </span>
                <p className="text-sm text-[#8A8A8A] font-light leading-[1.7]">
                  {opt.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== RETURNS POLICY ===== */}
      <section className="w-full bg-[#F0EFED] py-16 md:py-24 px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl mx-auto flex flex-col gap-10">
          <div>
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2A2A2A] mb-6 text-center">
              {t('shipping.returns.heading')}
            </h2>
            <p className="text-sm text-[#5A5A5A] font-light leading-[1.9] text-center max-w-lg mx-auto">
              {t('shipping.returns.desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {returnSteps.map((item) => (
              <div
                key={item.num}
                className="bg-white rounded-2xl p-6 flex flex-col items-center text-center gap-3"
              >
                <span className="text-[10px] tracking-[0.15em] uppercase text-[#7B1113] font-medium">
                  {t('common.step')} {item.num}
                </span>
                <h3 className="font-serif text-lg font-bold text-[#2A2A2A]">
                  {item.title}
                </h3>
                <p className="text-sm text-[#8A8A8A] font-light leading-[1.7]">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#E8E8E6]">
            <h3 className="font-serif text-lg font-bold text-[#2A2A2A] mb-4">
              {t('shipping.conditions.heading')}
            </h3>
            <ul className="flex flex-col gap-3 text-sm text-[#5A5A5A] font-light leading-[1.8]">
              {conditionsOk.map((c) => (
                <li key={c} className="flex items-start gap-3">
                  <i className="ri-check-line text-[#2D6A4F] mt-0.5 shrink-0" />
                  {c}
                </li>
              ))}
              {conditionsNot.map((c) => (
                <li key={c} className="flex items-start gap-3">
                  <i className="ri-close-line text-[#7B1113] mt-0.5 shrink-0" />
                  {c}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#E8E8E6]">
            <h3 className="font-serif text-lg font-bold text-[#2A2A2A] mb-3">
              {t('shipping.refundTimeline.heading', 'Refund Timeline')}
            </h3>
            <p className="text-sm text-[#5A5A5A] font-light leading-[1.8]">
              {t(
                'shipping.refundTimeline.desc',
                'Once your returned item is received and inspected, approved refunds are issued within 7–10 working days to your original payment method (or via bKash/bank transfer for Cash on Delivery orders). You will receive an email confirmation once your refund has been processed.',
              )}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#E8E8E6]">
            <h3 className="font-serif text-lg font-bold text-[#2A2A2A] mb-3">
              {t('shipping.cancellation.heading', 'Order Cancellation')}
            </h3>
            <p className="text-sm text-[#5A5A5A] font-light leading-[1.8]">
              {t(
                'shipping.cancellation.desc',
                'You may cancel your order free of charge at any time before it has been dispatched, by contacting support@rxkseoul.com or WhatsApp with your order number. Once an order has been dispatched for delivery, it can no longer be cancelled — our standard Return Policy above applies instead after delivery.',
              )}
            </p>
          </div>
        </div>
      </section>

      <SharedFooter />
    </main>
  );
}